import Fuse from "fuse.js";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "./Command";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";

interface MultiSelectProps {
    children: React.ReactNode;
    title?: string;
    placeholder?: string;
    onChange: (values: string[]) => void;
    hasSelectAll?: boolean;
    className?: string;
    options: Array<{
        label: string;
        value: string;
        icon?: React.ComponentType<{ className?: string }>;
    }>;
    showCounts?: boolean;
}

const MultiSelectComponent: React.ForwardRefRenderFunction<
    HTMLButtonElement,
    MultiSelectProps
> = (
    {
        children,
        title,
        onChange,
        hasSelectAll,
        className,
        options,
        showCounts = false,
    },
    ref,
) => {
    const isInitialMount = useRef(true);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const fuse = new Fuse(options, {
        keys: ["label"],
        threshold: 0.3,
    });

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return; // skip the effect on mount
        }
        onChange(selectedValues);
    }, [selectedValues]);

    const handleSelectAll = useCallback(() => {
        if (selectedValues.length === options.length) setSelectedValues([]);
        else setSelectedValues(options.map((o) => o.value));
    }, [options, selectedValues]);

    const filteredOptions =
        searchQuery === ""
            ? options
            : fuse.search(searchQuery).map((result) => result.item);

    return (
        <Popover>
            <PopoverTrigger asChild>{children}</PopoverTrigger>
            <PopoverContent className="h-[300px] w-[200px] p-0" align="start">
                <Command>
                    <CommandInput
                        placeholder={title}
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        {hasSelectAll && (
                            <>
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={handleSelectAll}
                                        className="justify-center text-center"
                                    >
                                        Select All
                                    </CommandItem>
                                </CommandGroup>
                                <CommandSeparator />
                            </>
                        )}
                        <CommandGroup>
                            {filteredOptions.map((option) => {
                                const isSelected =
                                    selectedValues.indexOf(option.value) !== -1;
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => {
                                            if (isSelected) {
                                                setSelectedValues((sv) =>
                                                    sv.filter(
                                                        (s) =>
                                                            s !== option.value,
                                                    ),
                                                );
                                            } else {
                                                setSelectedValues((sv) => [
                                                    ...sv,
                                                    option.value,
                                                ]);
                                            }
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                        />
                                        {option.icon && (
                                            <option.icon className="mr-2 h-4 w-4 text-middleground" />
                                        )}
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => setSelectedValues([])}
                                        className="justify-center text-center"
                                    >
                                        Clear selection
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export const MultiSelectV2 = React.forwardRef(MultiSelectComponent);
